/**
 * Componente React Móvil de Aficionado para la dinámica "Medallas Tradiciones Perronas 2026" (Tile 52 / tpx2026).
 * Rediseñado con la identidad phygital de Xolos (Rojo #dc2626 y dorado), fondo multimedia (video o imagen),
 * formulario de 5 campos, animación de caja sorpresa y pase digital de alta resolución sin alertas invasivas.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Confetti from 'react-confetti';
import { InitializeAnalytics } from '../../../utilities/Analytics';
import {
    GetPublicConfig,
    RegisterParticipant,
    TrackMetric,
    GetOrCreateSessionId,
    ValidateParticipantData,
    MaskPhone,
    MaskEmail,
    SavePassToLocal,
    FormatMexicoDate,
    DownloadPassCard,
    PurgeExpiredPasses,
    SaveMultiplePassesToLocal,
    RecoverPass
} from '../../../utilities/BL/Xtp';

const TERMS_TEXT_DEFAULT = `TÉRMINOS Y CONDICIONES - Dinámica Boletos Xolos Femenil
1. Organizador: El Club Tijuana Xoloitzcuintles de Caliente es el único responsable organizador de la dinámica y de la coordinación para la entrega de boletos y acceso al estadio.
2. Proveedor Tecnológico: Five Digital Minds e Interaktia actúan exclusivamente como proveedores de la infraestructura tecnológica y quedan eximidos de responsabilidades relacionadas con la logística en tienda o acceso al estadio.
3. Participación y Requisito Exclusivo: Dinámica válida exclusivamente para aficionados que adquieran la edición conmemorativa del Jersey Alterno de Día de Muertos en tiendas oficiales Xolo Shops autorizadas y registren su ticket de compra oficial en la plataforma web.
4. Asignación de Boletos Ganadores y Cupo: La ventana de elegibilidad para la asignación de premios comprende exclusivamente a los primeros 333 registros participantes (o hasta agotar existencias). De entre dichos registros elegibles, se asignarán de manera aleatoria 20 boletos en total: 10 boletos para el partido de Xolos Femenil del 23 de Octubre y 10 boletos para el partido de Xolos Femenil del 31 de Octubre en el Estadio Caliente. Los registros posteriores a la posición 333 obtendrán su pase oficial de registro y autenticación, sin ser elegibles para la asignación de boletos.
5. Canje y Verificación Física Obligatoria: La asignación de cualquier boleto o beneficio queda estrictamente sujeta a la validación física del comprobante de compra original por parte del personal del Club Tijuana en taquilla o accesos oficiales, acreditando fehacientemente la adquisición del Jersey Alterno de Día de Muertos. De no acreditarse dicha compra, o en caso de detectarse alguna irregularidad, el boleto o beneficio será revocado sin derecho a reclamo o compensación y podrá ser reasignado por el comité organizador.
6. Condiciones del Beneficio: Cada pase es personal, único e intransferible. Los boletos o beneficios no son canjeables por dinero en efectivo ni transferibles a otras fechas. El aficionado podrá validar su beneficio presentando el código QR oficial de su pase digital o el Folio alfanumérico visible en el mismo ante el personal autorizado.
7. Descalificación: Intentos de registro duplicado de un mismo ticket, alteración de comprobantes de compra o uso de identidades falsas serán descalificados automáticamente.
8. Resoluciones: Las determinaciones del comité organizador del Club Tijuana Xoloitzcuintles de Caliente son definitivas e inapelables.`;


const PRIVACY_TEXT_DEFAULT = `AVISO DE PRIVACIDAD SIMPLIFICADO
Sus datos personales (Nombre Completo, Correo Electrónico, Teléfono celular / WhatsApp y Número de Ticket) serán tratados por el Club Tijuana Xoloitzcuintles de Caliente y la operadora tecnológica Five Digital Minds con la finalidad de:
1. Validar su participación en la dinámica mediante su comprobante de compra oficial.
2. Emitir y verificar su Pase Digital con código alfanumérico oficial y código QR seguro.
3. Contactarle para la entrega de boletos para los partidos de Xolos Femenil o brindarle información sobre beneficios y promociones oficiales del club.

Sus datos serán tratados con estrictas medidas de seguridad y confidencialidad. Al registrarse, usted autoriza el tratamiento de sus datos conforme a este aviso. Para más información sobre el ejercicio de sus derechos ARCO, consulte los canales oficiales del Club Tijuana Xoloitzcuintles de Caliente.`;

export const XtpFan = () => {
    const [searchParams] = useSearchParams();

    // 1. Detección y normalización de fuente (UTM / s)
    const source = useMemo(() => {
        const raw = searchParams.get('s') || searchParams.get('utm_source');
        if (raw === 'l') return 'xolo_shop_local';
        if (raw === 'e') return 'xolo_shop_estadio';
        if (raw === 'qr') return 'medalla_qr_direct';
        return raw || 'xolo_shop_local';
    }, [searchParams]);

    // 2. Estados principales de ciclo de vida
    const [phase, setPhase] = useState('form'); // 'form' | 'box' | 'pass'
    const [config, setConfig] = useState(null);
    const [storedPasses, setStoredPasses] = useState([]);
    const [activePassIndex, setActivePassIndex] = useState(0);
    const [sessionId, setSessionId] = useState('');

    // 3. Estados de formulario
    const [formData, setFormData] = useState({
        medalCode: '',
        ticketNumber: '',
        name: '',
        email: '',
        phone: '',
        acceptedTerms: false,
        commercialConsent: false
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 4. Estados de Caja Sorpresa
    const [isBoxOpening, setIsBoxOpening] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    // 5. Estados de Pase y Descarga
    const [activePass, setActivePass] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadSuccessToast, setDownloadSuccessToast] = useState(false);

    // 6. Modales Legales
    const [activeLegalModal, setActiveLegalModal] = useState(null); // 'terms' | 'privacy' | null

    // 7. Estados de Recuperación de Pases
    const [showRecoveryModal, setShowRecoveryModal] = useState(false);
    const [recoveryQuery, setRecoveryQuery] = useState('');
    const [isRecovering, setIsRecovering] = useState(false);
    const [recoveryError, setRecoveryError] = useState('');
    const [recoverySuccessMsg, setRecoverySuccessMsg] = useState('');

    // 8. Dimensiones de ventana para Confetti
    const [windowDimensions, setWindowDimensions] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 400,
        height: typeof window !== 'undefined' ? window.innerHeight : 800
    });

    const passCardRef = useRef(null);
    const formStartTrackedRef = useRef(false);

    // ========================================================
    // TELEMETRÍA DUAL: BASE DE DATOS + GTM (dataLayer) + GA4 (gtag)
    // ========================================================
    const trackEvent = (eventName) => {
        const sId = sessionId || GetOrCreateSessionId();
        try {
            TrackMetric(eventName, source, sId);
        } catch (e) {
            console.error('Local metric error:', e);
        }

        try {
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: `tpx2026_${eventName}`,
                    session_id: sId,
                    traffic_source: source,
                    timestamp: new Date().toISOString()
                });
            }
        } catch (e) {
            console.error('DataLayer tracking error:', e);
        }

        try {
            if (window.gtag) {
                window.gtag('event', `tpx2026_${eventName}`, {
                    session_id: sId,
                    event_category: 'tpx2026_dynamic',
                    event_label: eventName,
                    traffic_source: source
                });
            }
        } catch (e) {
            console.error('GA4 tracking error:', e);
        }
    };

    const handleInputFocus = () => {
        if (!formStartTrackedRef.current) {
            formStartTrackedRef.current = true;
            trackEvent('form_start');
        }
    };

    // ========================================================
    // EFECTOS DE INICIALIZACIÓN
    // ========================================================
    useEffect(() => {
        InitializeAnalytics();
        const sId = GetOrCreateSessionId();
        setSessionId(sId);

        // Registrar telemetría de visita al portal
        trackEvent('page_view');

        // Registrar escaneo de QR si viene parámetro en URL
        const hasQrParam = searchParams.get('s') || searchParams.get('src') === 'qr';
        if (hasQrParam) {
            trackEvent('qr_scan');
        }

        // Purgar inmediatamente pases locales que excedan el TTL estándar (7 días)
        const initialPasses = PurgeExpiredPasses(7);
        setStoredPasses(initialPasses);

        // Cargar configuración pública y sincronizar TTL dinámico del backend
        GetPublicConfig().then((res) => {
            if (res && res.success && res.data) {
                setConfig(res.data);
                const ttlDays = res.data.localStoragePassTtlDays || 7;
                const freshPasses = PurgeExpiredPasses(ttlDays);
                setStoredPasses(freshPasses);
            }
        });

        const handleResize = () => {
            setWindowDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [source]);

    // ========================================================
    // MANEJADORES DE FORMULARIO
    // ========================================================
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (fieldErrors[field]) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            });
        }
        if (generalError) {
            setGeneralError('');
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setGeneralError('');

        // Validación en cliente
        const validation = ValidateParticipantData(formData, config);
        if (!validation.isValid) {
            setFieldErrors(validation.errors);
            setGeneralError(validation.error || 'Por favor completa correctamente los campos obligatorios.');
            return;
        }

        setIsSubmitting(true);
        trackEvent('form_submit_start');

        try {
            const payload = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                medalCode: (formData.medalCode || formData.ticketNumber || '').trim().toUpperCase(),
                ticketNumber: formData.ticketNumber.trim().toUpperCase(),
                acceptedTerms: formData.acceptedTerms,
                commercialConsent: formData.commercialConsent,
                source: source,
                sessionId: sessionId
            };

            const response = await RegisterParticipant(payload, sessionId);

            if (response && response.success && response.passData) {
                const pass = response.passData;
                setActivePass(pass);

                // Persistir localmente con TTL
                const updatedPasses = SavePassToLocal(pass);
                setStoredPasses(updatedPasses);
                setActivePassIndex(0);

                trackEvent('register_complete');
                trackEvent('register_success');

                // Decidir siguiente fase: la caja sorpresa SOLO se muestra si es ganador
                const isWinner = Boolean(pass.isWinner);
                const shouldShowGiftBox = isWinner && (config?.showGiftBoxAnimation !== undefined
                    ? config.showGiftBoxAnimation
                    : true);

                if (shouldShowGiftBox) {
                    setPhase('box');
                    trackEvent('gift_box_shown');
                } else {
                    setPhase('pass');
                    if (isWinner) {
                        setShowConfetti(true);
                        setTimeout(() => setShowConfetti(false), 4500);
                    }
                }
            } else {
                // Mensaje discreto defensivo anti-enumeración
                setGeneralError(
                    response?.message ||
                    'No fue posible procesar el registro con los datos ingresados. Verifica tu número de ticket, o si ya lo habías registrado, puedes consultarlo en la opción de consulta.'
                );
                trackEvent('register_rejected');
            }
        } catch (err) {
            console.error('Error al procesar registro:', err);
            setGeneralError('Ocurrió un error inesperado al conectar con el servidor oficial. Por favor intenta de nuevo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ========================================================
    // MANEJADORES DE CAJA SORPRESA
    // ========================================================
    const handleBoxTap = () => {
        if (isBoxOpening) return;
        setIsBoxOpening(true);

        // Haptic feedback si el dispositivo lo soporta
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
            try { navigator.vibrate([60, 40, 80]); } catch (e) { /* ignore */ }
        }

        trackEvent('gift_box_opened');
        setShowConfetti(true);

        // Transición a la Tarjeta Dorada tras 1.5s
        setTimeout(() => {
            setIsBoxOpening(false);
            setPhase('pass');
            setTimeout(() => setShowConfetti(false), 4500);
        }, 1500);
    };

    // ========================================================
    // PASE ACTIVO Y VISIBILIDAD DE COMPONENTES
    // ========================================================
    const currentPass = activePass || storedPasses[activePassIndex];
    const showQr = config?.showPassQr !== undefined 
        ? config.showPassQr 
        : (currentPass?.showPassQr !== undefined ? currentPass.showPassQr : true);
    const showUserData = config?.showPassUserData !== undefined 
        ? config.showPassUserData 
        : (currentPass?.showPassUserData !== undefined ? currentPass.showPassUserData : true);
    const showLocationNote = config?.showPassLocationNote !== undefined 
        ? config.showPassLocationNote 
        : (currentPass?.showPassLocationNote !== undefined ? currentPass.showPassLocationNote : true);

    // ========================================================
    // MANEJADORES DE DESCARGA EN CANVAS 2D
    // ========================================================
    const handleDownloadPass = async () => {
        const passToDownload = currentPass;
        if (!passToDownload || isDownloading) return;

        setIsDownloading(true);
        try {
            const isWinner = Boolean(passToDownload.isWinner);

            // =========================================================================
            // [DESCARGA ANTERIOR DE GANADOR TEMPORALMENTE COMENTADA]
            // =========================================================================
            /*
            if (isWinner) {
                const qrSvgElement = passCardRef.current?.querySelector('svg');
                success = await DownloadPassCard(passToDownload, {
                    qrElement: qrSvgElement,
                    showPassQr: showQr,
                    showPassUserData: showUserData,
                    showPassLocationNote: showLocationNote,
                    campaignTitle: config?.campaignTitle || passToDownload?.campaignTitle,
                    winningPrizeDescription: passToDownload?.winningPrizeDescription || config?.winningPrizeDescription,
                    nonWinningMessage: passToDownload?.nonWinningMessage || config?.nonWinningMessage,
                    prizeName: passToDownload?.prizeName
                });
            }
            */

            // Mismo diseño y opciones de descarga que /passtpx (tanto ganadores como no ganadores)
            const success = await DownloadPassCard(passToDownload, {
                qrElementSelector: '#xtp-pass-card svg',
                bannerTitle: 'PASE PERSONAL EXCLUSIVO',
                hideHeader: true,
                hidePrize: true,
                hideLocationNote: true,
                sponsorLogoUrl: '/img/page/xolos-five-black.png',
                backgroundImageUrl: '/img/page/backgrounds/Fondo-SelfieFan-Varonil.jpeg'
            });

            if (success) {
                trackEvent('pass_saved');
                setDownloadSuccessToast(true);
                setTimeout(() => setDownloadSuccessToast(false), 3500);
            }
        } catch (err) {
            console.error('Error durante la descarga de la tarjeta:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    // ========================================================
    // NAVEGACIÓN ENTRE PASES GUARDADOS
    // ========================================================
    const handleSelectStoredPass = (index) => {
        if (index >= 0 && index < storedPasses.length) {
            setActivePassIndex(index);
            setActivePass(storedPasses[index]);
            setPhase('pass');
        }
    };

    // ========================================================
    // MANEJADORES DE RECUPERACIÓN OMNICANAL
    // ========================================================
    const handleOpenRecoveryModal = () => {
        setRecoveryError('');
        setRecoverySuccessMsg('');
        setRecoveryQuery('');
        setShowRecoveryModal(true);
        trackEvent('recovery_modal_opened');
    };

    const handleCloseRecoveryModal = () => {
        setShowRecoveryModal(false);
        setRecoveryError('');
        setRecoverySuccessMsg('');
    };

    const handleRecoverSubmit = async (e) => {
        e.preventDefault();
        if (!recoveryQuery.trim()) {
            setRecoveryError('Por favor ingresa tu correo, teléfono o folio.');
            return;
        }

        setIsRecovering(true);
        setRecoveryError('');
        setRecoverySuccessMsg('');
        trackEvent('recovery_submit_start');

        try {
            const response = await RecoverPass(recoveryQuery.trim(), sessionId);

            if (response && response.success) {
                const recoveredPasses = response.passes && response.passes.length > 0
                    ? response.passes
                    : (response.pass ? [response.pass] : []);

                if (recoveredPasses.length > 0) {
                    const updatedPasses = SaveMultiplePassesToLocal(recoveredPasses);
                    setStoredPasses(updatedPasses);
                    setActivePass(recoveredPasses[0]);
                    setActivePassIndex(0);

                    const count = recoveredPasses.length;
                    const successNotice = `¡Se ${count === 1 ? 'recuperó 1 pase' : `recuperaron ${count} pases`} exitosamente!`;
                    setRecoverySuccessMsg(successNotice);
                    setShowConfetti(true);
                    trackEvent('pass_recovery');
                    trackEvent('recovery_success');

                    setTimeout(() => {
                        setShowRecoveryModal(false);
                        setPhase('pass');
                        setTimeout(() => setShowConfetti(false), 4500);
                    }, 1400);
                    return;
                }
            }

            setRecoveryError(
                response?.message ||
                'No encontramos pases registrados con los datos proporcionados. Verifica tu información o registra tu ticket.'
            );
            trackEvent('recovery_not_found');
        } catch (err) {
            console.error('Error al recuperar pase:', err);
            setRecoveryError('Ocurrió un error al consultar tus pases. Por favor intenta de nuevo.');
        } finally {
            setIsRecovering(false);
        }
    };

    const bgUrl = config?.bannerImageLgUrl || '/img/page/backgrounds/background-womens-day-2026-bold.png';
    const isBgVideo = bgUrl && (bgUrl.endsWith('.mp4') || bgUrl.endsWith('.webm'));

    // ==========================================
    // MODALES LEGALES (ESTILO MVP XOLOS ZONE)
    // ==========================================
    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-75 d-flex align-items-center justify-content-center px-3" style={{ zIndex: 11000 }}>
                <div className="card w-100 bg-white border-0 rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button type="button" className="btn-close" onClick={() => setter(null)}></button>
                    </div>
                    <div className="text-secondary small" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.5' }}>
                        {content}
                    </div>
                    <button type="button" className="btn btn-danger w-100 py-2 rounded-3 fw-bold mt-4" onClick={() => setter(null)}>
                        Cerrar
                    </button>
                </div>
            </div>
        );
    };

    // ========================================================
    // RENDERIZADO
    // ========================================================
    return (
        <div
            className="XtpFan"
            style={!isBgVideo ? {
                backgroundImage: `url('${bgUrl}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            } : undefined}
        >
            {/* Fondo de Video Silencioso en Bucle si está configurado */}
            {isBgVideo && (
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="XtpFan__BgVideo"
                    src={bgUrl}
                />
            )}

            {/* Capa de Difuminado y Contraste */}
            <div className="XtpFan__Overlay"></div>

            {/* Lluvia de Confetti */}
            {showConfetti && (
                <Confetti
                    width={windowDimensions.width}
                    height={windowDimensions.height}
                    numberOfPieces={220}
                    recycle={false}
                    colors={['#d4af37', '#fde047', '#dc2626', '#b91c1c', '#ffffff', '#1e1b4b']}
                    style={{ position: 'fixed', top: 0, left: 0, zIndex: 100001, pointerEvents: 'none' }}
                />
            )}

            <div className="XtpFan__Container">
                {/* 1. MEMBRETE / LOGO SUPERIOR OFICIAL */}
                <header className="XtpFan__Header text-center pt-2 pb-1">
                    <img
                        src={config?.bannerImageMdUrl || '/img/page/xolos-five-bicolor.png'}
                        alt="Club Tijuana Xoloitzcuintles"
                        className="XtpFan__Header__Logo img-fluid d-block mx-auto mb-2"
                        style={{ maxHeight: '70px', objectFit: 'contain' }}
                        onError={(e) => { e.target.src = '/img/page/xolos-five-bicolor.png'; }}
                    />
                </header>

                {/* 2. BOTÓN PÍLDORA PARA VER PASES YA CONSULTADOS */}
                {storedPasses.length > 0 && phase !== 'box' && (
                    <div className="text-center py-1 px-3 mb-2 bg-transparent border-0">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-warning text-uppercase fw-bold rounded-pill px-3 py-1 shadow-sm"
                            style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}
                            onClick={() => {
                                setActivePass(storedPasses[0]);
                                setActivePassIndex(0);
                                setPhase('pass');
                            }}
                        >
                            <i className="bi bi-award-fill me-1"></i> Ver mis pases consultados ({storedPasses.length})
                        </button>
                    </div>
                )}

                {/* 3. TÍTULO Y 4. SUBTÍTULO */}
                {phase === 'form' && (
                    <div className="text-center mb-3">
                        <h1 className="font-cinzel text-white text-3xl font-black mb-1">
                            {/* {config?.campaignTitle || 'Medallas Tradiciones Perronas 2026'} */}
                            Tradiciones Perronas
                        </h1>
                        <p className="text-white small mb-0">
                            {/* {config?.campaignSubtitle || 'Club Tijuana Xoloitzcuintles de Caliente'} */}
                            Registra tu ticket de compra y participa por un regalo especial
                        </p>
                    </div>
                )}

                {/* ====================================================
                 * FASE 1: FORMULARIO DE REGISTRO & VALIDACIÓN
                 * ==================================================== */}
                {phase === 'form' && (
                    <div className="XtpFan__Card">
                        <form onSubmit={handleFormSubmit} noValidate>
                            {/* 1. Número de Ticket */}
                            <div className="XtpFan__FormGroup">
                                <label htmlFor="xtp-ticketNumber">
                                    No. de ticket {config?.ticketRequiredLength ? `(${config.ticketRequiredLength} caracteres)` : ''} <span className="req">*</span>
                                </label>
                                <input
                                    id="xtp-ticketNumber"
                                    type="text"
                                    className={`XtpFan__Input ${fieldErrors.ticketNumber ? 'is-invalid' : ''}`}
                                    value={formData.ticketNumber}
                                    onChange={(e) => handleInputChange('ticketNumber', e.target.value.toUpperCase())}
                                    onFocus={handleInputFocus}
                                    disabled={isSubmitting}
                                    maxLength={config?.ticketRequiredLength ? config.ticketRequiredLength : 50}
                                    autoComplete="off"
                                />
                                {fieldErrors.ticketNumber && (
                                    <div className="XtpFan__ErrorText">{fieldErrors.ticketNumber}</div>
                                )}
                            </div>

                            {/* 2. Nombre Completo */}
                            <div className="XtpFan__FormGroup">
                                <label htmlFor="xtp-name">
                                    Nombre completo <span className="req">*</span>
                                </label>
                                <input
                                    id="xtp-name"
                                    type="text"
                                    className={`XtpFan__Input ${fieldErrors.name ? 'is-invalid' : ''}`}
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    onFocus={handleInputFocus}
                                    disabled={isSubmitting}
                                    maxLength={120}
                                    autoComplete="name"
                                />
                                {fieldErrors.name && (
                                    <div className="XtpFan__ErrorText">{fieldErrors.name}</div>
                                )}
                            </div>

                            {/* 3. Correo Electrónico */}
                            <div className="XtpFan__FormGroup">
                                <label htmlFor="xtp-email">
                                    Correo electrónico <span className="req">*</span>
                                </label>
                                <input
                                    id="xtp-email"
                                    type="email"
                                    className={`XtpFan__Input ${fieldErrors.email ? 'is-invalid' : ''}`}
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    onFocus={handleInputFocus}
                                    disabled={isSubmitting}
                                    maxLength={120}
                                    autoComplete="email"
                                />
                                {fieldErrors.email && (
                                    <div className="XtpFan__ErrorText">{fieldErrors.email}</div>
                                )}
                            </div>

                            {/* 4. Teléfono Celular / WhatsApp (10 dígitos) */}
                            <div className="XtpFan__FormGroup">
                                <label htmlFor="xtp-phone">
                                    Teléfono celular / WhatsApp (10 dígitos) <span className="req">*</span>
                                </label>
                                <input
                                    id="xtp-phone"
                                    type="tel"
                                    className={`XtpFan__Input ${fieldErrors.phone ? 'is-invalid' : ''}`}
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    onFocus={handleInputFocus}
                                    disabled={isSubmitting}
                                    maxLength={10}
                                    autoComplete="tel"
                                />
                                {fieldErrors.phone && (
                                    <div className="XtpFan__ErrorText">{fieldErrors.phone}</div>
                                )}
                            </div>

                            {/* Checkbox Obligatorio Términos & Privacidad */}
                            <div className="XtpFan__FormGroup mt-3">
                                <label className="XtpFan__CheckboxLabel">
                                    <input
                                        type="checkbox"
                                        checked={formData.acceptedTerms}
                                        onChange={(e) => handleInputChange('acceptedTerms', e.target.checked)}
                                        disabled={isSubmitting}
                                    />
                                    <span>
                                        He leído y acepto los{' '}
                                        <a
                                            href="#terminos"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setActiveLegalModal('terms');
                                            }}
                                        >
                                            Términos y Condiciones
                                        </a>{' '}
                                        y el{' '}
                                        <a
                                            href="#privacidad"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setActiveLegalModal('privacy');
                                            }}
                                        >
                                            Aviso de Privacidad
                                        </a><span className="req">*</span>
                                    </span>
                                </label>
                                {fieldErrors.acceptedTerms && (
                                    <div className="XtpFan__ErrorText">{fieldErrors.acceptedTerms}</div>
                                )}
                            </div>

                            {/* Checkbox Opcional Consentimiento Comercial */}
                            <div className="XtpFan__FormGroup">
                                <label className="XtpFan__CheckboxLabel">
                                    <input
                                        type="checkbox"
                                        checked={formData.commercialConsent}
                                        onChange={(e) => handleInputChange('commercialConsent', e.target.checked)}
                                        disabled={isSubmitting}
                                    />
                                    <span>
                                        Deseo recibir beneficios, promociones y noticias exclusivas de Club Tijuana
                                    </span>
                                </label>
                            </div>

                            {/* Botón de Enviar en Rojo Xolos */}
                            <div className="mt-4">
                                {config && config.isCampaignActive === false && (
                                    <div className="alert alert-warning py-2 px-3 small border-0 rounded-3 mb-3 text-center fw-semibold shadow-sm">
                                        <i className="bi bi-pause-circle-fill me-1 text-danger fs-6 align-middle"></i>
                                        Los registros se encuentran temporalmente pausados por el comité organizador.
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="btn btn-danger w-100 py-3 rounded-3 fw-bold text-uppercase fs-6 shadow-lg d-flex align-items-center justify-content-center gap-2 mb-2"
                                    style={{
                                        backgroundColor: config && config.isCampaignActive === false ? '#6b7280' : '#dc2626',
                                        borderColor: config && config.isCampaignActive === false ? '#6b7280' : '#dc2626',
                                        letterSpacing: '0.04em'
                                    }}
                                    disabled={isSubmitting || (config && config.isCampaignActive === false)}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            <span>Registrando mi ticket...</span>
                                        </>
                                    ) : config && config.isCampaignActive === false ? (
                                        <>
                                            <i className="bi bi-pause-fill fs-5"></i>
                                            <span>Registros Pausados</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-ticket-perforated-fill fs-5"></i>
                                            <span>Registrar mi ticket</span>
                                        </>
                                    )}
                                </button>
                                {generalError && (
                                    <div className="text-danger text-center small mt-2 fw-semibold" role="alert">
                                        {generalError}
                                    </div>
                                )}
                            </div>

                            {/* Enlace discreto de consulta/recuperación */}
                            <div className="text-center pt-3 mt-2 border-top border-secondary border-opacity-25">
                                <button
                                    type="button"
                                    className="btn btn-link text-white text-decoration-none small fw-bold p-0"
                                    onClick={handleOpenRecoveryModal}
                                >
                                    <i className="bi bi-search text-warning me-1"></i>
                                    ¿Ya registraste tu ticket? Consulta tu pase aquí
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* ====================================================
                 * FASE 2: CAJA SORPRESA SVG INTERACTIVA (EN FLUJO NORMAL)
                 * ==================================================== */}
                {phase === 'box' && (
                    <div className="XtpFan__GiftBoxFlow">
                        <h2 className="XtpFan__GiftBoxText font-cinzel gold-text">
                            ¡Tenemos una sorpresa para ti!
                        </h2>
                        <p className="XtpFan__GiftBoxHint">
                            <i className="bi bi-hand-index-thumb-fill"></i>
                            Toca la caja para descubrirlo
                        </p>

                        <div
                            className={`XtpFan__GiftBoxSvgContainer ${isBoxOpening ? 'is-opening' : 'is-idle'}`}
                            onClick={handleBoxTap}
                            role="button"
                            tabIndex={0}
                            aria-label="Abrir caja sorpresa"
                        >
                            <svg
                                viewBox="0 0 240 240"
                                width="240"
                                height="240"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <defs>
                                    <linearGradient id="xtpBoxBody" x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0%" stopColor="#ef4444" />
                                        <stop offset="50%" stopColor="#dc2626" />
                                        <stop offset="100%" stopColor="#7f1d1d" />
                                    </linearGradient>

                                    <linearGradient id="xtpGoldRibbon" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#b38728" />
                                        <stop offset="50%" stopColor="#fde047" />
                                        <stop offset="100%" stopColor="#d4af37" />
                                    </linearGradient>

                                    <radialGradient id="xtpFlare" cx="50%" cy="50%" r="50%">
                                        <stop offset="0%" stopColor="#fde047" stopOpacity="0.9" />
                                        <stop offset="40%" stopColor="#d4af37" stopOpacity="0.5" />
                                        <stop offset="100%" stopColor="#b38728" stopOpacity="0" />
                                    </radialGradient>
                                </defs>

                                <circle
                                    className="xtp-glow"
                                    cx="120"
                                    cy="120"
                                    r="90"
                                    fill="url(#xtpFlare)"
                                    opacity="0"
                                />

                                <ellipse cx="120" cy="215" rx="75" ry="12" fill="rgba(0,0,0,0.5)" />

                                <rect
                                    x="45"
                                    y="105"
                                    width="150"
                                    height="100"
                                    rx="10"
                                    fill="url(#xtpBoxBody)"
                                    stroke="#d4af37"
                                    strokeWidth="2"
                                />

                                <rect
                                    x="106"
                                    y="105"
                                    width="28"
                                    height="100"
                                    fill="url(#xtpGoldRibbon)"
                                />

                                <g className="xtp-lid" style={{ transformOrigin: '120px 105px' }}>
                                    <rect
                                        x="36"
                                        y="80"
                                        width="168"
                                        height="28"
                                        rx="6"
                                        fill="#b91c1c"
                                        stroke="#d4af37"
                                        strokeWidth="2.5"
                                    />
                                    <rect
                                        x="106"
                                        y="80"
                                        width="28"
                                        height="28"
                                        fill="url(#xtpGoldRibbon)"
                                    />

                                    <g className="xtp-bow" style={{ transformOrigin: '120px 80px' }}>
                                        <path
                                            d="M120 80 C80 50 65 30 90 25 C115 20 120 70 120 80 Z"
                                            fill="url(#xtpGoldRibbon)"
                                            stroke="#b38728"
                                            strokeWidth="1.5"
                                        />
                                        <path
                                            d="M120 80 C160 50 175 30 150 25 C125 20 120 70 120 80 Z"
                                            fill="url(#xtpGoldRibbon)"
                                            stroke="#b38728"
                                            strokeWidth="1.5"
                                        />
                                        <circle
                                            cx="120"
                                            cy="80"
                                            r="11"
                                            fill="#fef08a"
                                            stroke="#b38728"
                                            strokeWidth="2"
                                        />
                                        <path
                                            d="M115 85 L95 115 L108 112 L118 92 Z"
                                            fill="url(#xtpGoldRibbon)"
                                        />
                                        <path
                                            d="M125 85 L145 115 L132 112 L122 92 Z"
                                            fill="url(#xtpGoldRibbon)"
                                        />
                                    </g>
                                </g>
                            </svg>
                        </div>
                    </div>
                )}

                {/* ====================================================
                 * FASE 3: TARJETA DORADA DE PASE DIGITAL O AGRADECIMIENTO
                 * ==================================================== */}
                {phase === 'pass' && currentPass && (() => {
                    const isWinner = Boolean(currentPass.isWinner);
                    const nonWinnersMode = currentPass.nonWinnersDisplayMode || config?.nonWinnersDisplayMode || 'SHOW_DIGITAL_PASS';
                    const isMessageOnly = !isWinner && nonWinnersMode === 'SHOW_MESSAGE_ONLY';

                    return (
                        <div className="XtpFan__PassView">
                            {/* Selector de pase si tiene múltiples */}
                            {storedPasses.length > 1 && (
                                <div className="XtpFan__PassNavigation">
                                    <button
                                        type="button"
                                        onClick={() => handleSelectStoredPass(activePassIndex - 1)}
                                        disabled={activePassIndex <= 0}
                                        aria-label="Pase anterior"
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                    <div className="d-flex flex-column align-items-center">
                                        <span className="counter">
                                            Registro {activePassIndex + 1} de {storedPasses.length}
                                        </span>
                                        <div className="d-flex gap-1 mt-1">
                                            {storedPasses.map((p, idx) => (
                                                <span
                                                    key={p.passCode || idx}
                                                    className={`XtpFan__PaginatorDot ${idx === activePassIndex ? 'active' : ''}`}
                                                    onClick={() => handleSelectStoredPass(idx)}
                                                    title={`Ver registro ${p.passCode || idx + 1}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleSelectStoredPass(activePassIndex + 1)}
                                        disabled={activePassIndex >= storedPasses.length - 1}
                                        aria-label="Siguiente pase"
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>
                            )}

                            {/* =========================================================================
                             * [DISEÑO ANTERIOR NO GANADOR TEMPORALMENTE COMENTADO - MODO SOLO MENSAJE]
                             * =========================================================================
                            {isMessageOnly ? (
                                <>
                                    <div className="XtpFan__Card text-center py-4 px-3">
                                        <div className="mb-3">
                                            <i className="bi bi-patch-check-fill text-warning" style={{ fontSize: '3.8rem' }}></i>
                                        </div>
                                        <h2 className="font-cinzel text-white fs-3 fw-bold mb-2">
                                            {currentPass.nonWinningTitle || config?.nonWinningTitle || '¡Gracias por registrarte!'}
                                        </h2>
                                        <p className="text-light mb-4 px-2" style={{ lineHeight: '1.6' }}>
                                            {currentPass.nonWinningMessage || config?.nonWinningMessage || 'Tu ticket de compra ha sido registrado y certificado en la plataforma oficial del Club Tijuana. ¡Sigue apoyando con pasión al Xolaje!'}
                                        </p>

                                        <div className="bg-dark bg-opacity-50 p-3 rounded-4 border border-secondary border-opacity-25 mb-1 text-start">
                                            {currentPass.isWinner && currentPass.prizeName && (
                                                <div className="d-flex justify-content-between py-1 border-bottom border-secondary border-opacity-25 small">
                                                    <span className="text-warning fw-bold">Premio:</span>
                                                    <span className="fw-bold text-warning">{currentPass.prizeName}</span>
                                                </div>
                                            )}
                                            <div className="d-flex justify-content-between py-1 border-bottom border-secondary border-opacity-25 small">
                                                <span className="text-light opacity-75">Nombre:</span>
                                                <span className="fw-bold text-white">{(currentPass.name || 'AFICIONADO').toUpperCase()}</span>
                                            </div>
                                            <div className="d-flex justify-content-between py-1 border-bottom border-secondary border-opacity-25 small">
                                                <span className="text-light opacity-75">No. de Ticket:</span>
                                                <span className="fw-bold text-light font-monospace">{currentPass.ticketNumber}</span>
                                            </div>
                                            <div className="d-flex justify-content-between py-1 border-bottom border-secondary border-opacity-25 small">
                                                <span className="text-light opacity-75">Folio de Pase:</span>
                                                <span className="fw-bold text-light font-monospace">{currentPass.passCode}</span>
                                            </div>
                                            <div className="d-flex justify-content-between py-1 small">
                                                <span className="text-light opacity-75">Fecha:</span>
                                                <span className="text-light">{FormatMexicoDate(currentPass.createdAtUtc || currentPass.savedAt)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-outline-light flex-fill py-2 rounded-3 fw-bold small"
                                            onClick={() => {
                                                setFormData({
                                                    medalCode: '',
                                                    ticketNumber: '',
                                                    name: '',
                                                    email: '',
                                                    phone: '',
                                                    acceptedTerms: false,
                                                    commercialConsent: false
                                                });
                                                setPhase('form');
                                            }}
                                        >
                                            <i className="bi bi-plus-circle me-1"></i>
                                            Registrar otro ticket
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-outline-warning flex-fill py-2 rounded-3 fw-bold small"
                                            onClick={handleOpenRecoveryModal}
                                        >
                                            <i className="bi bi-search text-warning me-1"></i>
                                            Recuperar más pases
                                        </button>
                                    </div>
                                </>
                            ) : null}
                            */}

                            {/* PASE DIGITAL OFICIAL (DISEÑO /passtpx PARA NO GANADORES Y PASE GANADOR PARA GANADORES) */}
                            <>
                                {/* Botón Superior: Guardar Pase en la Galería con leyenda informativa */}
                                <div className="text-center mb-3 mx-auto" style={{ maxWidth: '440px' }}>
                                    <p className="small mb-2 text-white" style={{ fontSize: '0.85rem' }}>
                                        Guárdalo o toma captura de pantalla
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-warning w-100 py-3 rounded-3 fw-black text-uppercase shadow-lg fs-6 d-flex align-items-center justify-content-center gap-2"
                                        style={{
                                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                            color: '#ffffff',
                                            border: 'none',
                                            letterSpacing: '0.04em'
                                        }}
                                        onClick={handleDownloadPass}
                                        disabled={isDownloading}
                                    >
                                        {isDownloading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                <span>Guardando...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-camera-fill fs-5"></i>
                                                <span>Guardar Pase en mi Galería</span>
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* =========================================================================
                                 * [DISEÑO ANTERIOR GANADOR TEMPORALMENTE COMENTADO - TARJETA DORADA ANTERIOR]
                                 * =========================================================================
                                <div className="XtpFan__PassCard" id="xtp-pass-card-old-winner">
                                    <h1 className="font-cinzel text-4xl font-black mb-1 gold-text tracking-wider">
                                        ¡Felicidades!
                                    </h1>

                                    <div className="XtpFan__Divider"></div>

                                    <div className="mb-3 px-1">
                                        <p className="fs-6 sun-glare-fix leading-snug text-dark">
                                            {currentPass.winningPrizeDescription || (currentPass.prizeName ? `¡Felicidades! Has ganado: ${currentPass.prizeName}. Presenta este pase oficial ante el staff.` : '¡Ganaste un premio especial con tu Xolo Folio al registrarte!')}
                                        </p>
                                    </div>

                                    <div className="XtpFan__ShimmerBanner">
                                        <div className="XtpFan__ShimmerBannerOverlay"></div>
                                        <h2 className="font-cinzel fs-4 font-black text-white text-uppercase tracking-widest mb-0 drop-shadow">
                                            Pase Ganador Oficial
                                        </h2>
                                        <p className="text-white small fw-bold mt-1 mb-2 tracking-wide">
                                            Tradiciones Perronas
                                        </p>
                                        {currentPass.prizeName && (
                                            <div className="badge bg-warning text-dark fw-bold text-wrap px-3 py-1 mb-2 shadow-sm" style={{ fontSize: '0.82rem', letterSpacing: '0.02em', lineHeight: '1.3' }}>
                                                Premio: {currentPass.prizeName}
                                            </div>
                                        )}
                                        <div className="XtpFan__FolioBadge">
                                            {currentPass.passCode || '#TPX26-00000'}
                                        </div>
                                    </div>

                                    {showQr && (
                                        <div className="d-flex justify-content-center my-3 p-2 bg-light rounded-3 border">
                                            <QRCodeSVG
                                                value={currentPass.verificationToken || currentPass.passCode || 'XTP-2026'}
                                                size={175}
                                                bgColor="#ffffff"
                                                fgColor="#172554"
                                                level="Q"
                                                includeMargin={true}
                                                imageSettings={{
                                                    src: '/img/favicon/Big.png',
                                                    x: undefined,
                                                    y: undefined,
                                                    height: 42,
                                                    width: 42,
                                                    excavate: true
                                                }}
                                            />
                                        </div>
                                    )}

                                    {showUserData && (
                                        <div className="small text-muted mb-2 text-center">
                                            <span className="fw-bold text-dark">{(currentPass.name || 'AFICIONADO').toUpperCase()}</span>
                                            <br />
                                            <span style={{ fontSize: '0.8rem' }}>
                                                {MaskEmail(currentPass.email)} | {MaskPhone(currentPass.phone)}
                                            </span>
                                            <br />
                                            <span style={{ fontSize: '0.8rem' }} className="fw-semibold text-dark">
                                                Ticket: {currentPass.ticketNumber || 'N/A'}
                                            </span>
                                            <br />
                                            <span style={{ fontSize: '0.75rem' }} className="text-muted">
                                                {FormatMexicoDate(currentPass.createdAtUtc || currentPass.savedAt)}
                                            </span>
                                        </div>
                                    )}

                                    {showLocationNote && (
                                        <div className="XtpFan__SecurityBox">
                                            <p>
                                                MUESTRA ESTE PASE O UNA CAPTURA EN TIENDA OFICIAL O ESTADIO
                                            </p>
                                        </div>
                                    )}

                                    <div className="mt-3">
                                        <p className="text-muted text-uppercase mb-2 fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.15em' }}>
                                            Powered by
                                        </p>
                                        <div className="XtpFan__SponsorLogos">
                                            <img
                                                src="/img/page/xolos-five-bicolor.png"
                                                alt="Club Tijuana"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                 * ========================================================================= */}

                                {/* =========================================================================
                                 * NUEVO DISEÑO DE PASE OFICIAL (IDÉNTICO A /passtpx)
                                 * ========================================================================= */}
                                <div className="XtpFan__PassCard" id="xtp-pass-card" ref={passCardRef}>
                                    <div className="XtpFan__ShimmerBanner">
                                        <div className="XtpFan__ShimmerBannerOverlay"></div>
                                        <h2 className="font-cinzel fs-4 font-black text-white text-uppercase tracking-widest mb-0 drop-shadow">
                                            Pase Personal Exclusivo
                                        </h2>
                                        <p className="text-white small fw-bold mt-1 mb-2 tracking-wide">
                                            Tradiciones Perronas
                                        </p>
                                        <div className="XtpFan__FolioBadge">
                                            {currentPass.passCode || '#TPX26-00000'}
                                        </div>
                                    </div>

                                    {/* QR con Logotipo Personalizado y Nivel Q */}
                                    <div className="d-flex justify-content-center my-3 p-2 bg-light rounded-3 border">
                                        <QRCodeSVG
                                            value={currentPass.verificationToken || currentPass.passCode || 'XTP-2026'}
                                            size={175}
                                            bgColor="#ffffff"
                                            fgColor="#172554"
                                            level="Q"
                                            includeMargin={true}
                                            imageSettings={{
                                                src: '/img/favicon/Big.png',
                                                x: undefined,
                                                y: undefined,
                                                height: 42,
                                                width: 42,
                                                excavate: true
                                            }}
                                        />
                                    </div>

                                    {/* Datos Protegidos del Aficionado */}
                                    <div className="small text-muted mb-2 text-center">
                                        <span className="fw-bold text-dark fs-6">
                                            {(currentPass.name || 'AFICIONADO').toUpperCase()}
                                        </span>
                                        <br />
                                        <span>
                                            {MaskEmail(currentPass.email)} • {MaskPhone(currentPass.phone)}
                                        </span>
                                        <br />
                                        <span className="font-monospace text-dark fw-bold">
                                            Ticket: {currentPass.ticketNumber}
                                        </span>
                                        <br />
                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            Registrado el: {FormatMexicoDate(currentPass.createdAtUtc || currentPass.savedAt)}
                                        </span>
                                    </div>

                                    {/* Patrocinador y Logotipo Oficial (Estilo /passtpx) */}
                                    <div className="mt-3">
                                        <p className="text-muted text-uppercase mb-2 fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.15em' }}>
                                            Powered by
                                        </p>
                                        <div className="XtpFan__SponsorLogos">
                                            <img
                                                src="/img/page/xolos-five-black.png"
                                                alt="Club Tijuana"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* =========================================================================
                                 * [DISEÑO ANTERIOR NO GANADOR TEMPORALMENTE COMENTADO - TARJETA ANTERIOR]
                                 * =========================================================================
                                <div className="XtpFan__PassCard" id="xtp-pass-card-old-nonwinner">
                                    <h1 className="font-cinzel text-4xl font-black mb-1 gold-text tracking-wider">¡Felicidades!</h1>
                                    <div className="XtpFan__Divider"></div>
                                    <div className="mb-3 px-1">
                                        <p className="fs-6 sun-glare-fix leading-snug text-dark">
                                            {currentPass.nonWinningMessage || `Tu ${currentPass.itemNoun || config?.itemNoun || 'Xolo Folio'} ha sido validado y autenticado oficialmente.`}
                                        </p>
                                    </div>
                                    <div className="XtpFan__ShimmerBanner">
                                        <div className="XtpFan__ShimmerBannerOverlay"></div>
                                        <h2 className="font-cinzel fs-4 font-black text-white text-uppercase tracking-widest mb-0 drop-shadow">Pase Exclusivo Personal</h2>
                                        <p className="text-white small fw-bold mt-1 mb-2 tracking-wide">Tradiciones Perronas</p>
                                        <div className="XtpFan__FolioBadge">{currentPass.passCode || '#TPX26-00000'}</div>
                                    </div>
                                    ...
                                </div>
                                ========================================================================= */}

                                    {/* Botón Inferior: Guardar Pase en la Galería */}
                                    <div className="text-center mt-3 mx-auto" style={{ maxWidth: '440px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-warning w-100 py-3 rounded-3 fw-black text-uppercase shadow-lg fs-6 d-flex align-items-center justify-content-center gap-2"
                                            style={{
                                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                color: '#ffffff',
                                                border: 'none',
                                                letterSpacing: '0.04em'
                                            }}
                                            onClick={handleDownloadPass}
                                            disabled={isDownloading}
                                        >
                                            {isDownloading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    <span>Generando imagen de alta resolución...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-camera-fill fs-5"></i>
                                                    <span>Guardar Pase en mi Galería</span>
                                                </>
                                            )}
                                        </button>
                                        <p className="small mt-2 mb-0 text-white" style={{ fontSize: '0.85rem' }}>
                                            Guárdalo o toma captura de pantalla
                                        </p>
                                    </div>

                                    {/* Toast de confirmación de descarga */}
                                    {downloadSuccessToast && (
                                        <div className="alert alert-success text-center mt-3 py-2 small shadow-sm">
                                            <i className="bi bi-check-circle-fill me-1"></i>
                                            ¡Pase guardado exitosamente en tus descargas!
                                        </div>
                                    )}

                                    {/* Acciones Secundarias */}
                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-outline-light flex-fill py-2 rounded-3 fw-bold small d-flex align-items-center justify-content-center gap-1"
                                            style={{ borderColor: 'rgba(255,255,255,0.25)' }}
                                            onClick={() => {
                                                setFormData({
                                                    medalCode: '',
                                                    ticketNumber: '',
                                                    name: '',
                                                    email: '',
                                                    phone: '',
                                                    acceptedTerms: false,
                                                    commercialConsent: false
                                                });
                                                setPhase('form');
                                            }}
                                        >
                                            <i className="bi bi-plus-circle me-1"></i>
                                            Registrar otro ticket
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-outline-warning flex-fill py-2 rounded-3 fw-bold small d-flex align-items-center justify-content-center gap-1"
                                            onClick={handleOpenRecoveryModal}
                                        >
                                            <i className="bi bi-search text-warning me-1"></i>
                                            Recuperar mi pase
                                        </button>
                                    </div>
                                </>
                        </div>
                    );
                })()}

                {/* 5. FOOTER ESTRICTO: "Hecho con ♥ por Interaktia" (CON SUBRAYADO) */}
                <footer className="XtpFan__Footer text-center py-3 mt-auto">
                    <p className="text-center FdmFooter FooterCredits mb-0">
                        <a
                            href="https://interaktia.com/?utm_source=xolo_folio_web&utm_medium=footer&utm_campaign=fdm_xolos"
                            target="_blank"
                            rel="noreferrer"
                            className="text-white small"
                            style={{ textDecoration: 'underline' }}
                        >
                            Hecho con ♥ por Interaktia
                        </a>
                    </p>
                </footer>
            </div>

            {/* ====================================================
             * MODALES LEGALES (ESTILO MVP XOLOS ZONE - FIJOS E INMUTABLES)
             * ==================================================== */}
            {activeLegalModal === 'terms' && renderLegalModal('Términos y Condiciones', TERMS_TEXT_DEFAULT, setActiveLegalModal)}
            {activeLegalModal === 'privacy' && renderLegalModal('Aviso de Privacidad', PRIVACY_TEXT_DEFAULT, setActiveLegalModal)}

            {/* ====================================================
             * MODAL INTERACTIVO: RECUPERACIÓN OMNICANAL DE PASES
             * ==================================================== */}
            {showRecoveryModal && (
                <div
                    className="XtpFan__ModalOverlay"
                    onClick={handleCloseRecoveryModal}
                >
                    <div
                        className="XtpFan__ModalContent"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="XtpFan__ModalContent__Header">
                            <h3 className="font-cinzel">
                                <i className="bi bi-search text-warning me-2"></i>
                                Recuperar pase
                            </h3>
                            <button
                                type="button"
                                onClick={handleCloseRecoveryModal}
                                aria-label="Cerrar modal"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="XtpFan__ModalContent__Body">
                            <p className="text-white small mb-3">
                                Ingresa tu correo electrónico, número de teléfono (10 dígitos) o el folio para consultar y obtener tu pase.
                            </p>

                            {recoveryError && (
                                <div className="alert alert-danger py-2 small mb-3" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                    {recoveryError}
                                </div>
                            )}

                            {recoverySuccessMsg && (
                                <div className="alert alert-success py-2 small mb-3" role="alert">
                                    <i className="bi bi-check-circle-fill me-1"></i>
                                    {recoverySuccessMsg}
                                </div>
                            )}

                            <form onSubmit={handleRecoverSubmit}>
                                <div className="XtpFan__FormGroup mb-3">
                                    <div className="XtpFan__RecoveryInputWrapper">
                                        <i className="bi bi-search icon"></i>
                                        <input
                                            id="xtp-recovery-query"
                                            type="text"
                                            className="XtpFan__Input"
                                            value={recoveryQuery}
                                            onChange={(e) => setRecoveryQuery(e.target.value)}
                                            disabled={isRecovering}
                                            autoFocus
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <button
                                        type="submit"
                                        className="XtpFan__BtnGold"
                                        disabled={isRecovering || !recoveryQuery.trim()}
                                    >
                                        {isRecovering ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                <span>Buscando...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>Consultar ahora</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XtpFan;
